'use client';

import {
  Alert,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { ConnectorType } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { FormTextField } from '@/components/form/formTextField';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import type { AddCatalogWizardState } from '../addCatalogWizardState';

export const InlineAddGcpConnectorStep = (
  props: StepProps<AddCatalogWizardState>
) => {
  const dal = useDAL();
  const { workspaceId } = props.wizardState;
  const [acknowledged, setAcknowledged] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const connectorState =
    props.wizardState.connectorSource?.mode === 'new'
      ? props.wizardState.connectorSource.connectorState
      : null;

  if (!connectorState || !workspaceId) return null;

  const {
    gcpProjectId,
    discoveryServiceAccountName,
    dataAccessServiceAccountName,
    connectorName,
  } = connectorState;

  const updateConnectorState = (updates: Partial<typeof connectorState>) => {
    if (props.wizardState.connectorSource?.mode !== 'new') return;
    props.setWizardState({
      ...props.wizardState,
      connectorSource: {
        ...props.wizardState.connectorSource,
        connectorState: { ...connectorState, ...updates },
      },
    });
  };

  const isMissingFields =
    !gcpProjectId ||
    !discoveryServiceAccountName ||
    !dataAccessServiceAccountName ||
    !connectorName.trim();

  const canCreate = !isMissingFields && acknowledged && !creating;

  const handleCreate = async () => {
    setError(null);
    setCreating(true);
    try {
      const result = await dal.lakehouse.createConnector(workspaceId, {
        name: connectorName,
        connectorType: ConnectorType.CONNECTOR_TYPE_GCP_PROJECT_CONNECTOR,
        gcpProjectProperties: {
          gcpProjectId,
          discoveryServiceAccountName,
          dataAccessServiceAccountName,
        },
      });
      props.setWizardState({
        ...props.wizardState,
        connectorSource: {
          mode: 'new',
          connectorState,
          createdConnectorId: result.id,
        },
        connectorExternalId: gcpProjectId,
      });
      props.setNextStep(props.currentStep.next?.[0]);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to create connector'
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <StepContainer
      stepperIndex={1}
      stepperLabels={props.stepperLabels}
      canGoNext={() => canCreate}
      nextButtonText={creating ? 'Creating...' : 'Create Connector & Continue'}
      nextButtonLoadingText='Creating...'
      backButtonText='Back'
      onBackClick={props.back}
      onNextClick={handleCreate}
      sx={{ overflow: 'auto', paddingX: '0px', paddingY: 'min(60px, 4vw)' }}
    >
      <Stack gap='min(60px, 4vw)' divider={<Divider />}>
        {/* Connector Details */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>Connector Details</Typography>
              <Typography variant='body2' color='text.secondary'>
                Name the connector and specify the GCP project it connects to.
              </Typography>
            </Stack>

            <FormTextField
              title='Connector Name'
              size='small'
              placeholder='e.g., GCP Project my-project'
              value={connectorName}
              onChange={(e) =>
                updateConnectorState({ connectorName: e.target.value })
              }
            />

            <FormTextField
              title='GCP Project ID'
              size='small'
              placeholder='e.g., my-gcp-project-123'
              value={gcpProjectId}
              onChange={(e) => {
                const newProjectId = e.target.value;
                const updates: Partial<typeof connectorState> = {
                  gcpProjectId: newProjectId,
                };
                const currentDefault = `GCP Project ${gcpProjectId}`;
                if (!connectorName || connectorName === currentDefault) {
                  updates.connectorName = newProjectId
                    ? `GCP Project ${newProjectId}`
                    : '';
                }
                updateConnectorState(updates);
              }}
            />
          </Stack>
        </Box>

        {/* Service Accounts */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>Service Accounts</Typography>
              <Typography variant='body2' color='text.secondary'>
                Specify the GCP service account names that Eon will use in your
                GCP project for discovery and data access.
              </Typography>
            </Stack>

            <FormTextField
              title='Discovery Service Account Name'
              size='small'
              placeholder='e.g., eon-datalake-discovery-sa'
              value={discoveryServiceAccountName}
              onChange={(e) =>
                updateConnectorState({
                  discoveryServiceAccountName: e.target.value,
                })
              }
            />

            <FormTextField
              title='Data Access Service Account Name'
              size='small'
              placeholder='e.g., eon-datalake-data-access-sa'
              value={dataAccessServiceAccountName}
              onChange={(e) =>
                updateConnectorState({
                  dataAccessServiceAccountName: e.target.value,
                })
              }
            />
          </Stack>
        </Box>

        {/* Deployment Confirmation */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>Deployment</Typography>
              <Typography variant='body2' color='text.secondary'>
                Confirm that the required service accounts have been configured
                in your GCP project before creating the connector.
              </Typography>
            </Stack>

            {gcpProjectId && (
              <Alert severity='info'>
                <Typography variant='body2'>
                  Ensure the following service accounts are configured in GCP
                  project <strong>{gcpProjectId}</strong> with the required
                  permissions for Eon:
                </Typography>
                <Typography
                  variant='body2'
                  component='ul'
                  sx={{ mt: '8px', pl: '16px' }}
                >
                  <li>
                    <code>{discoveryServiceAccountName}</code> (discovery)
                  </li>
                  <li>
                    <code>{dataAccessServiceAccountName}</code> (data access)
                  </li>
                </Typography>
              </Alert>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  size='small'
                />
              }
              label={
                <Typography variant='body2'>
                  I have configured the service accounts in my GCP project
                </Typography>
              }
            />
          </Stack>
        </Box>
      </Stack>

      {error && (
        <Box paddingX='min(100px, 6vw)' paddingTop='24px'>
          <Alert severity='error' sx={{ maxWidth: '540px' }}>
            <Typography variant='body2'>{error}</Typography>
          </Alert>
        </Box>
      )}
    </StepContainer>
  );
};
